import { useMutation, useQuery } from "@apollo/client";
import _ from "lodash";
import { Button, LoadingSpinner, Modal } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import updateProgramConfiguration from "../../../graphql/mutations/updateProgramConfiguration";
import { loadUiComponent } from "../../../graphql/queries/loadUiComponent";
import { paths } from "../../../routes/paths";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import "./app-configurations.scss";

export default function AppConfigurationsForm({ isEditPage }) {
  const [appConfigurations, setAppConfigurations] = useState({});
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [hasEmptyFields, setHasEmptyFields] = useState(true);
  const [uiSchema, setUiSchema] = useState(null);
  const [jsonSchema, setJsonSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });

  const [updateProgramConfigurationMutation, { loading }] = useMutation(
    updateProgramConfiguration
  );

  const {
    programConfigurations,
    updateProgramConfigurations,
    globalState: { programConfig },
  } = useContext(GlobalConfigContext);

  const { refetch: fetchSchemas } = useQuery(loadUiComponent, {
    variables: { slug: "app-configuration" },
    skip: true,
    errorPolicy: "all",
  });

  useEffect(() => {
    const loadSchemas = async () => {
      try {
        const res = await fetchSchemas();
        if (res?.data?.loadUiComponent) {
          setUiSchema(res.data.loadUiComponent.uiSchema);
          setJsonSchema(res.data.loadUiComponent.inputSchema);
        } else {
          setSchemaError(true);
        }
      } catch (error) {
        console.error("Failed to load app configuration schema:", error);
        setSchemaError(true);
      }
    };
    loadSchemas();
  }, [fetchSchemas]);

  useEffect(() => {
    const programConfigData = _.find(programConfigurations, {
      key: "app_configurations",
    });
    if (programConfigData !== null && programConfigData !== undefined) {
      setAppConfigurations(
        programConfigData.value?.data === null ? {} : programConfigData.value?.data
      );
    }
  }, [programConfigurations]);

  useEffect(() => {
    const isEmptyValue = (value) =>
      value === null || value === undefined || value === "";
    const hasEmpty = _.some(appConfigurations, (value) => isEmptyValue(value));
    setHasEmptyFields(hasEmpty);
    if (hasEmpty) {
      openWarningToast();
    }
  }, [appConfigurations]);

  const handleSaveConfirmation = async () => {
    const params = {
      variables: {
        input: {
          slug: "app_configurations",
          data: JSON.stringify(appConfigurations),
        },
        id: programConfig?.id,
      },
    };

    try {
      const res = await updateProgramConfigurationMutation(params);
      if (res?.data?.updateProgramConfiguration) {
        updateProgramConfigurations(
          res?.data?.updateProgramConfiguration?.configurations
        );
        openSuccessToast();
      }
    } catch {
      openErrorToast();
    }

    setConfirmationModalOpen(false);
  };

  const openSuccessToast = () => {
    setToast({
      open: true,
      type: "success",
      title: "Settings updated successfully!",
      description: "You have just saved the user limit settings",
      navigateToSettings: true,
    });
  };

  const openWarningToast = () => {
    setToast({
      open: true,
      type: "warning",
      title: "Fill all mandatory fields",
    });
  };

  const openErrorToast = () => {
    setToast({
      open: true,
      type: "error",
      title: "Something went wrong while saving the user limit settings.",
      description: "Try again later.",
    });
  };

  const navigate = useNavigate();

  const navigateToSystemSettings = () => {
    navigate(`/${paths.system_setting}`);
  };

  const schemasLoaded = uiSchema && jsonSchema;
  const isLoading = !schemasLoaded && !schemaError;

  return (
    <div className="container appConfigurationsForm">
      <div className="wrapper">
        {isLoading ? (
          <div className="loading-container">
            <LoadingSpinner
              size={{
                height: "2rem",
                width: "2rem",
              }}
            />
          </div>
        ) : schemaError ? (
          <div className="error-container">
            <p>Failed to load App Configuration settings. Please try again later.</p>
          </div>
        ) : (
          <JsonFormsWrapper
            data={appConfigurations}
            onChange={(data) => setAppConfigurations(data)}
            uischema={uiSchema}
            jsonschema={jsonSchema}
          />
        )}
        <div className="button-container">
          <Button
            size="medium"
            text="Discard"
            variant="secondary"
            className="mr-2"
            onClick={navigateToSystemSettings}
          />
          <Button
            size="medium"
            text="Save"
            variant="primary"
            disabled={!isEditPage || hasEmptyFields}
            onClick={() => setConfirmationModalOpen(true)}
          />
        </div>

        <Modal
          showModal={confirmationModalOpen}
          onCloseModal={() => setConfirmationModalOpen(false)}
          primaryButtonProps={{
            size: "medium",
            text: "Save",
            variant: "primary",
            disabled: loading,
            onClick: handleSaveConfirmation,
          }}
          secondaryButtonProps={{
            size: "medium",
            text: "Back to settings",
            variant: "secondary",
            onClick: () => setConfirmationModalOpen(false),
          }}
          showTertiaryButton={false}
          showLink={false}
        >
          <div className="wrap">
            <h2>Save this settings?</h2>
            <p>Are you sure you want to update the user limit settings?</p>
          </div>
        </Modal>
        <ToastNotification
          {...toast}
          handleClose={() => {
            setToast({ ...toast, open: false });
            if (toast.navigateToSettings) {
              navigateToSystemSettings();
            }
          }}
        />
      </div>
    </div>
  );
}
